import { useEffect, useState } from "react";
import { Plus, Edit2, X, Trash2, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

const ROLES = ["alumno", "profesor", "psicologo", "padre", "administrativo", "ejecutivo"];
const GRADES = [
    "1ro Primaria A", "1ro Primaria B",
    "2do Primaria A", "2do Primaria B",
    "3ro Primaria A", "3ro Primaria B",
    "4to Primaria A", "4to Primaria B",
    "5to Primaria A", "5to Primaria B",
    "6to Primaria A", "6to Primaria B",
    "1ro Secundaria A", "1ro Secundaria B",
    "2do Secundaria A", "2do Secundaria B",
    "3ro Secundaria A", "3ro Secundaria B",
    "4to Secundaria A", "4to Secundaria B",
    "5to Secundaria A", "5to Secundaria B", "5to Secundaria C",
];
const PAGE_SIZE = 10;

export default function EjecutivoUsuarios() {
    const [users, setUsers] = useState([]);
    const [filterSchool, setFilterSchool] = useState("");
    const [filterRole, setFilterRole] = useState("");
    const [schools, setSchools] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [modalTop, setModalTop] = useState(200);

    async function load() {
        const params = {};
        if (filterRole) params.role = filterRole;
        if (filterSchool) params.school = filterSchool;
        const [u, s] = await Promise.all([
            api.get("/users", { params }),
            api.get("/ejecutivo/schools"),
        ]);
        setUsers(u.data || []);
        setSchools(s.data || []);
        setPage(1);
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSchool, filterRole]);

    async function deactivate(id) {
        if (!window.confirm("¿Desactivar usuario?")) return;
        await api.delete(`/users/${id}`);
        toast.success("Usuario desactivado");
        load();
    }

    const filteredUsers = users.filter((u) => {
        if (!u.active) return false;
        if (search && !u.name.toLowerCase().includes(search.toLowerCase()) &&
            !u.email.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
    const paginated = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="ejecutivo-usuarios-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-semibold">Gestión de Usuarios</h1>
                    <p className="text-edu-body">Filtra por colegio y luego por rol.</p>
                </div>
                <button
                    onClick={() => setCreating(true)}
                    className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2"
                    data-testid="create-user-button"
                >
                    <Plus size={14} /> Crear Usuario
                </button>
            </div>

            <div className="flex flex-wrap gap-3 mb-5">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-edu-muted font-semibold mb-1 block">1. Colegio</label>
                    <select className="edu-input min-w-[220px]" value={filterSchool}
                        onChange={(e) => { setFilterSchool(e.target.value); setPage(1); }} data-testid="filter-school">
                        <option value="">Todos los colegios</option>
                        {schools.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-edu-muted font-semibold mb-1 block">2. Rol</label>
                    <select className="edu-input" value={filterRole}
                        onChange={(e) => { setFilterRole(e.target.value); setPage(1); }} data-testid="filter-role">
                        <option value="">Todos los roles</option>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
            </div>

            {/* Búsqueda */}
            <div className="relative mb-5">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-edu-muted" />
                <input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    placeholder="Buscar por nombre o email..."
                    className="edu-input w-full pl-10"
                />
            </div>

            <div className="edu-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm hidden md:table" data-testid="admins-table-desktop">
                        <thead className="bg-edu-soft border-b border-edu">
                            <tr className="text-left">
                                <th className="px-4 py-3">Nombre</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Rol</th>
                                <th className="px-4 py-3">Colegio</th>
                                <th className="px-4 py-3">{filterRole === "profesor" ? "CV" : "Grado"}</th>
                                <th className="px-4 py-3">Estado</th>
                                <th className="px-4 py-3 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-6 text-edu-muted">Sin usuarios.</td></tr>
                            )}
                            {paginated.map((u) => (
                                <tr key={u.id} className="border-b border-edu/50">
                                    <td className="px-4 py-3 font-medium">{u.name}</td>
                                    <td className="px-4 py-3 text-edu-body">{u.email}</td>
                                    <td className="px-4 py-3 capitalize">{u.role}</td>
                                    <td className="px-4 py-3 text-edu-muted">{u.school || "—"}</td>
                                    <td className="px-4 py-3">
                                        {u.role === "profesor" ? (
                                            u.cv_file_id ? (
                                                <a href={fileUrl(u.cv_file_id)} target="_blank" rel="noreferrer"
                                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                    data-testid={`cv-download-${u.id}`}>
                                                    <Download size={12} /> Descargar CV
                                                </a>
                                            ) : <span className="text-xs text-edu-muted">Sin CV</span>
                                        ) : <span className="text-edu-muted">{u.grade || "—"}</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`edu-pill ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                                            {u.active ? "Activo" : "Inactivo"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={(e) => { setModalTop(e.clientY - 50); setEditing(u); }}
                                            className="px-3 py-1 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center gap-1 mr-2"
                                            data-testid={`edit-${u.id}`}>
                                            <Edit2 size={12} /> Editar
                                        </button>
                                        <button onClick={() => deactivate(u.id)}
                                            className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center gap-1"
                                            data-testid={`deactivate-${u.id}`}>
                                            <Trash2 size={12} /> Desactivar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-edu/50" data-testid="admins-cards-mobile">
                    {paginated.length === 0 && <div className="text-center py-8 text-edu-muted">Sin usuarios.</div>}
                    {paginated.map((u) => (
                        <div key={u.id} className="p-4 flex flex-col gap-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold truncate">{u.name}</div>
                                    <div className="text-xs text-edu-muted truncate">{u.email}</div>
                                </div>
                                <span className={`edu-pill flex-shrink-0 ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-600"}`}>
                                    {u.active ? "Activo" : "Inactivo"}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                                <span className="edu-pill bg-edu-navy/10 text-edu-navy capitalize">{u.role}</span>
                                {u.school && <span className="edu-pill bg-gray-100 text-gray-700 truncate max-w-[180px]">{u.school}</span>}
                                {u.role === "profesor" && u.cv_file_id ? (
                                    <a href={fileUrl(u.cv_file_id)} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-800">
                                        <Download size={11} /> CV
                                    </a>
                                ) : u.role === "profesor" ? (
                                    <span className="text-edu-muted">Sin CV</span>
                                ) : u.grade ? (
                                    <span className="edu-pill bg-gray-100 text-gray-700">{u.grade}</span>
                                ) : null}
                            </div>
                            <div className="flex gap-2 pt-1">
                                <button
                                    onClick={(e) => { setModalTop(e.clientY - 50); setEditing(u); }}
                                    className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center justify-center gap-1"
                                    data-testid={`m-edit-${u.id}`}>
                                    <Edit2 size={12} /> Editar
                                </button>
                                <button onClick={() => deactivate(u.id)}
                                    className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 inline-flex items-center justify-center gap-1"
                                    data-testid={`m-deactivate-${u.id}`}>
                                    <Trash2 size={12} /> Desactivar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-edu">
                        <span className="text-xs text-edu-muted">
                            {filteredUsers.length} usuarios · Página {page} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronLeft size={16} />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {(creating || editing) && (
                <UserModal
                    user={editing}
                    modalTop={modalTop}
                    schools={schools}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function UserModal({ user, schools, onClose, onSaved, modalTop }) {
    const isEdit = !!user;
    const [form, setForm] = useState(
        user ?? { name: "", email: "", password: "", role: "alumno", school: "", grade: "", learning_style: "" }
    );
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/users/${user.id}`, {
                    name: form.name, email: form.email, role: form.role,
                    school: form.school, grade: form.grade, learning_style: form.learning_style,
                });
            } else {
                await api.post("/users", form);
            }
            toast.success("Guardado");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose}>
            <div
                className="edu-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto absolute left-1/2 -translate-x-1/2"
                style={{ top: Math.min(modalTop, window.innerHeight - 520) }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{isEdit ? "Editar usuario" : "Crear usuario"}</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <Input label="Nombre" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                    <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                    {!isEdit && <Input label="Contraseña" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />}
                    <Select label="Rol" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={ROLES} />
                    {form.role !== "ejecutivo" && (     <Select label="Colegio" value={form.school || ""} onChange={(v) => setForm({ ...form, school: v })} options={["", ...schools.map((s) => s.name)]} /> )}
                    {form.role === "alumno" && (
                        <Select label="Grado" value={form.grade || ""} onChange={(v) => setForm({ ...form, grade: v })} options={["", ...GRADES]} />
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">Cancelar</button>
                        <button onClick={save} disabled={saving} className="edu-btn-primary px-4 py-2 text-sm" data-testid="user-save">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text" }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">{label}</span>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="edu-input w-full" />
        </label>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-edu-muted font-semibold mb-1.5 block">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="edu-input w-full">
                {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
            </select>
        </label>
    );
}
